import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import ActivityCard from './ActivityCard';
import { activitiesApi, filesApi, projectFormsApi } from '../services/api';
import { compressImages } from '../utils/imageCompression';
import { limitFilesForUpload } from '../utils/uploadLimits';
import { renameFile } from '../utils/fileNaming';
import ImageViewer from './ImageViewer';

const getFormFields = (form) => (
  form?.template?.form_schema?.fields
  || form?.form_schema?.fields
  || form?.template_form_schema?.fields
  || []
);

const isPersistedAttachmentId = (attachmentId) => /^\d+$/.test(String(attachmentId || ''));

const resolveInlineAttachmentUrl = (attachment) => {
  if (!attachment) {
    return '';
  }

  if (typeof attachment.url === 'string' && attachment.url) {
    return attachment.url;
  }

  if (typeof attachment.blob_path === 'string' && /^https?:\/\//.test(attachment.blob_path)) {
    return attachment.blob_path;
  }

  return '';
};

function ReadOnlyFormAttachmentField({ items = [], fieldType }) {
  const attachments = useMemo(() => (
    Array.isArray(items) ? items.filter(Boolean) : []
  ), [items]);
  const [attachmentUrls, setAttachmentUrls] = useState({});
  const [preview, setPreview] = useState({ open: false, url: '', title: '', currentIndex: 0 });

  const loadAttachmentUrl = useCallback(async (attachment) => {
    if (!attachment) {
      return '';
    }

    const inlineUrl = resolveInlineAttachmentUrl(attachment);
    if (inlineUrl) {
      return inlineUrl;
    }

    if (!isPersistedAttachmentId(attachment.photo_id)) {
      return '';
    }

    const response = await projectFormsApi.getPhotoUrl(attachment.photo_id);
    return response.data.url;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const preloadUrls = async () => {
      const loadedUrls = {};

      await Promise.all(attachments.map(async (attachment) => {
        try {
          const url = await loadAttachmentUrl(attachment);
          if (url) {
            loadedUrls[attachment.photo_id] = url;
          }
        } catch (error) {
          console.error('Failed to load form attachment URL:', error);
        }
      }));

      if (!cancelled && Object.keys(loadedUrls).length > 0) {
        setAttachmentUrls(prev => ({ ...prev, ...loadedUrls }));
      }
    };

    if (attachments.length > 0) {
      preloadUrls();
    }

    return () => {
      cancelled = true;
    };
  }, [attachments, loadAttachmentUrl]);

  const openAttachment = useCallback(async (attachment, index) => {
    try {
      const cachedUrl = attachmentUrls[attachment.photo_id] || resolveInlineAttachmentUrl(attachment);
      const url = cachedUrl || await loadAttachmentUrl(attachment);
      if (!url) {
        return;
      }

      setAttachmentUrls(prev => ({ ...prev, [attachment.photo_id]: url }));

      if (fieldType === 'pdf') {
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (newWindow) {
          newWindow.opener = null;
        }
        return;
      }

      setPreview({
        open: true,
        url,
        title: attachment.file_name || 'Photo',
        currentIndex: index,
      });
    } catch (error) {
      console.error('Failed to open form attachment:', error);
    }
  }, [attachmentUrls, fieldType, loadAttachmentUrl]);

  const handlePreviewNavigate = useCallback(async (direction) => {
    if (attachments.length === 0) {
      return;
    }

    const newIndex = direction === 'next'
      ? (preview.currentIndex + 1) % attachments.length
      : (preview.currentIndex - 1 + attachments.length) % attachments.length;
    const nextAttachment = attachments[newIndex];

    try {
      const cachedUrl = attachmentUrls[nextAttachment.photo_id] || resolveInlineAttachmentUrl(nextAttachment);
      const url = cachedUrl || await loadAttachmentUrl(nextAttachment);
      if (!url) {
        return;
      }

      setAttachmentUrls(prev => ({ ...prev, [nextAttachment.photo_id]: url }));
      setPreview({
        open: true,
        url,
        title: nextAttachment.file_name || 'Photo',
        currentIndex: newIndex,
      });
    } catch (error) {
      console.error('Failed to navigate form attachment preview:', error);
    }
  }, [attachmentUrls, attachments, loadAttachmentUrl, preview.currentIndex]);

  if (attachments.length === 0) {
    return <Typography variant="body2" color="text.secondary">No response</Typography>;
  }

  if (fieldType === 'photo') {
    const allImages = attachments.map((attachment) => ({
      ...attachment,
      url: attachmentUrls[attachment.photo_id] || resolveInlineAttachmentUrl(attachment),
    }));

    return (
      <>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          {attachments.map((attachment, index) => {
            const url = attachmentUrls[attachment.photo_id] || resolveInlineAttachmentUrl(attachment);
            return (
              <Box
                key={attachment.photo_id || `${attachment.file_name}-${index}`}
                onClick={() => openAttachment(attachment, index)}
                sx={{
                  width: 88,
                  height: 88,
                  borderRadius: 1,
                  overflow: 'hidden',
                  border: '1px solid',
                  borderColor: 'divider',
                  cursor: 'pointer',
                  bgcolor: 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title={attachment.file_name || 'Photo'}
              >
                {url ? (
                  <Box
                    component="img"
                    src={url}
                    alt={attachment.file_name || 'Photo'}
                    sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <Typography variant="caption" sx={{ px: 1, textAlign: 'center' }}>
                    {attachment.file_name || 'Photo'}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>
        <ImageViewer
          open={preview.open}
          onClose={() => setPreview({ open: false, url: '', title: '', currentIndex: 0 })}
          imageUrl={preview.url}
          fileName={preview.title}
          allImages={allImages}
          currentIndex={preview.currentIndex}
          onNavigate={handlePreviewNavigate}
          readOnly={true}
          canShare={false}
        />
      </>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
      {attachments.map((attachment, index) => (
        <Button
          key={attachment.photo_id || `${attachment.file_name}-${index}`}
          variant="outlined"
          size="small"
          onClick={() => openAttachment(attachment, index)}
        >
          {attachment.file_name || `File ${index + 1}`}
        </Button>
      ))}
    </Box>
  );
}

const getFormValue = (formData, field) => {
  if (!formData || typeof formData !== 'object' || !field) {
    return undefined;
  }

  const fieldId = field.id;
  if (fieldId !== undefined && fieldId !== null) {
    if (Object.prototype.hasOwnProperty.call(formData, fieldId)) {
      return formData[fieldId];
    }

    const fieldIdString = String(fieldId);
    if (Object.prototype.hasOwnProperty.call(formData, fieldIdString)) {
      return formData[fieldIdString];
    }
  }

  if (field.name && Object.prototype.hasOwnProperty.call(formData, field.name)) {
    return formData[field.name];
  }

  const normalizedLabelKey = (field.label || '').toLowerCase().replace(/\s+/g, '_');
  if (normalizedLabelKey && Object.prototype.hasOwnProperty.call(formData, normalizedLabelKey)) {
    return formData[normalizedLabelKey];
  }

  return undefined;
};

const renderReadOnlyFormField = (field, value) => {
  const emptyState = <Typography variant="body2" color="text.secondary">No response</Typography>;

  if (value === undefined || value === null || value === '') {
    return emptyState;
  }

  if (field.type === 'checkbox') {
    return <Chip size="small" color={value ? 'success' : 'default'} label={value ? 'Yes' : 'No'} />;
  }

  if (field.type === 'signature') {
    const signatureImage = typeof value === 'object' ? value.signature : value;
    const signerName = typeof value === 'object' ? (value.name || value.signer_name || value.printed_name) : '';

    if (!signatureImage) {
      return emptyState;
    }

    return (
      <Box>
        <Box
          component="img"
          src={signatureImage}
          alt={field.label || 'Signature'}
          sx={{
            maxWidth: '100%',
            maxHeight: 180,
            objectFit: 'contain',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'background.paper',
            p: 1,
          }}
        />
        {signerName && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Signed by: {signerName}
          </Typography>
        )}
      </Box>
    );
  }

  if (field.type === 'table' && Array.isArray(value)) {
    const columns = field.columns || [];

    if (value.length === 0) {
      return emptyState;
    }

    return (
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.id || column.name || column.label}>{column.label || column.name}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {value.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {columns.map((column) => {
                const columnKey = column.id ?? column.name;
                return (
                  <TableCell key={column.id || column.name || column.label}>
                    {row?.[columnKey] ?? row?.[String(columnKey)] ?? ''}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  if (field.type === 'photo' || field.type === 'pdf') {
    const items = Array.isArray(value) ? value : [value];

    if (items.length === 0) {
      return emptyState;
    }

    return <ReadOnlyFormAttachmentField items={items} fieldType={field.type} />;
  }

  if (Array.isArray(value)) {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {value.map((item, index) => (
          <Chip key={`${String(item)}-${index}`} size="small" variant="outlined" label={String(item)} />
        ))}
      </Box>
    );
  }

  if (typeof value === 'object') {
    return (
      <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem' }}>
        {JSON.stringify(value, null, 2)}
      </Box>
    );
  }

  return (
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {String(value)}
    </Typography>
  );
};

const ActivityDetails = ({ 
  activityId, 
  workers = [], 
  onClose,
  onEdit,
  onDelete,
  initialActivityData = null,
  readOnly = false,
  resolvePublicPhotoUrl = null,
  onOpenPlan = null,
}) => {
  const [activity, setActivity] = useState(initialActivityData || null);
  const [loading, setLoading] = useState(!initialActivityData);
  const [notes, setNotes] = useState(initialActivityData?.activity_notes || []);
  const [photos, setPhotos] = useState(initialActivityData?.photos || []);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [completingActivity, setCompletingActivity] = useState(null);
  const [selectedForm, setSelectedForm] = useState(null);
  const [loadingForm, setLoadingForm] = useState(false);
  
  // Mention state
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const mentionDebounceRef = useRef(null);

  // Photo preview state
  const [photoPreview, setPhotoPreview] = useState({ open: false, url: '', title: '', allPhotos: [], currentIndex: 0 });
  const photoUrlCache = useRef({});

  const loadActivityData = useCallback(async () => {
    if (!activityId) return;

    const hasInitialActivityData = initialActivityData && initialActivityData.activity_id === activityId;

    if (readOnly) {
      // Public/share views are read-only and should not call authenticated APIs.
      if (hasInitialActivityData) {
        setActivity(initialActivityData);
        setNotes(initialActivityData.activity_notes || []);
        setPhotos(initialActivityData.photos || []);
        setLoading(false);
      } else {
        setLoading(false);
      }
      return;
    }

    // In the authenticated plan viewer we often start with a lightweight pin payload.
    // Seed the UI from it, but still fetch the full activity details so linked forms/documents appear.
    if (hasInitialActivityData) {
      setActivity(initialActivityData);
      setNotes(initialActivityData.activity_notes || []);
      setPhotos(initialActivityData.photos || []);
      setLoading(false);
    }

    try {
      if (!hasInitialActivityData) {
        setLoading(true);
      }

      setLoadingNotes(true);
      setLoadingPhotos(true);

      const [activityResponse, detailsResponse] = await Promise.all([
        activitiesApi.getById(activityId),
        activitiesApi.getDetails(activityId),
      ]);

      const nextActivity = activityResponse.data || {};
      const nextDetails = detailsResponse.data || {};

      setNotes(nextDetails.notes || nextActivity.activity_notes || []);
      setPhotos(nextDetails.photos || nextActivity.photos || []);
      setActivity((prev) => ({
        ...(prev || {}),
        ...nextActivity,
        documents: nextDetails.documents || nextActivity.documents || prev?.documents || [],
        forms: nextDetails.forms || nextActivity.forms || prev?.forms || [],
      }));
      
    } catch (error) {
      console.error('Error loading activity details:', error);
    } finally {
      setLoading(false);
      setLoadingNotes(false);
      setLoadingPhotos(false);
    }
  }, [activityId, initialActivityData, readOnly]);

  useEffect(() => {
    loadActivityData();
  }, [loadActivityData]);

  // Update internal state when initialActivityData changes (e.g., after editing from ImageViewer)
  // Use JSON.stringify to detect deep changes in the object
  const initialActivityDataJson = JSON.stringify(initialActivityData);
  useEffect(() => {
    if (initialActivityData && initialActivityData.activity_id === activityId) {
      setActivity(initialActivityData);
      setNotes(initialActivityData.activity_notes || []);
      setPhotos(initialActivityData.photos || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialActivityDataJson, activityId]);

  const handleToggleComplete = async (act) => {
    try {
      setCompletingActivity(act.activity_id);
      if (act.is_completed) {
        await activitiesApi.markAsIncomplete(act.activity_id);
      } else {
        await activitiesApi.markAsComplete(act.activity_id);
      }
      // Refresh activity data
      const response = await activitiesApi.getById(act.activity_id);
      setActivity(response.data);
    } catch (error) {
      console.error('Error updating activity status:', error);
    } finally {
      setCompletingActivity(null);
    }
  };

  const handleNoteChange = (id, value) => {
    setNewNote(value);
  };

  const handleMentionInput = (value, cursorPosition, id) => {
    const atIndex = value.lastIndexOf('@', cursorPosition);
    
    if (atIndex === -1) {
      setShowMentions(false);
      return;
    }
    
    if (atIndex === cursorPosition - 1) {
      setShowMentions(true);
      setMentionSuggestions(workers);
      return;
    }
    
    if (mentionDebounceRef.current) {
      clearTimeout(mentionDebounceRef.current);
    }
    
    mentionDebounceRef.current = setTimeout(() => {
      const searchTerm = value.substring(atIndex + 1, cursorPosition).toLowerCase();
      const filtered = workers.filter(worker => 
        worker.name.toLowerCase().includes(searchTerm)
      );
      setMentionSuggestions(filtered);
      setShowMentions(filtered.length > 0);
    }, 150);
  };

  const handleInsertMention = (worker, id) => {
    const atIndex = newNote.lastIndexOf('@');
    const beforeMention = newNote.substring(0, atIndex);
    const afterMention = newNote.substring(atIndex).replace(/@\w*/, `@${worker.name.replace(' ', '')}`);
    setNewNote(beforeMention + afterMention + ' ');
    setShowMentions(false);
  };

  const handlePhotoSelect = async (event, id) => {
    const { files: selectedFiles, warning } = limitFilesForUpload(event.target.files);
    if (warning) {
      console.warn(warning);
      return;
    }

    const validFiles = selectedFiles.filter(file => {
      if (file.type.startsWith('image/')) {
        if (file.size <= 10 * 1024 * 1024) {
          return true;
        } else {
          console.error('File size must be under 10MB');
          return false;
        }
      } else {
        console.error('Only image files are allowed');
        return false;
      }
    });

    const compressedFiles = await compressImages(validFiles);
    const renamedFiles = compressedFiles.map(file => renameFile(file));

    const newPhotos = renamedFiles.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    
    setSelectedPhotos(prev => [...prev, ...newPhotos]);
  };

  const handleRemovePhoto = (id, index) => {
    setSelectedPhotos(prev => {
      const updated = [...prev];
      if (updated[index]?.preview) {
        URL.revokeObjectURL(updated[index].preview);
      }
      updated.splice(index, 1);
      return updated;
    });
  };

  const handleNoteSubmit = async (id) => {
    if (!newNote.trim() && selectedPhotos.length === 0) return;

    try {
      setUploading(true);

      let response;
      if (selectedPhotos.length > 0) {
        const formData = new FormData();
        formData.append('content', newNote || 'Photo attachment');
        
        selectedPhotos.forEach((photo, index) => {
          formData.append(`photo_${index}`, photo.file);
        });

        response = await activitiesApi.createNoteWithPhotos(activityId, formData);
        
        selectedPhotos.forEach(photo => {
          if (photo.preview) {
            URL.revokeObjectURL(photo.preview);
          }
        });
      } else {
        response = await activitiesApi.createNote(activityId, newNote);
      }
      
      setNewNote('');
      setSelectedPhotos([]);
      
      if (response.data.note) {
        setNotes(prev => [...prev, response.data.note]);
      }
    } catch (error) {
      console.error('Error creating activity note:', error);
    } finally {
      setUploading(false);
    }
  };

  const handlePhotoPreview = async (photo, id) => {
    if (photo.open) {
      setPhotoPreview(photo);
      return;
    }
    
    try {
      // Collect all photos
      const allNotesPhotos = notes.reduce((acc, note) => {
        return acc.concat(note.photos || []);
      }, []);
      
      const linkedPhotos = (activity?.documents || []).filter((file) => file.file_type === 'photos');
      const allPhotos = [...photos, ...linkedPhotos, ...allNotesPhotos];
      const currentIndex = allPhotos.findIndex(p => 
        (photo.file_id ? p.file_id === photo.file_id : p.photo_id === photo.photo_id)
        || (p.url === photo.url && p.file_name === photo.file_name)
      );
      
      const cacheKey = photo.file_id ? `file-${photo.file_id}` : photo.photo_id;
      const url = photo.file_id && !readOnly
        ? (await filesApi.getUrl(photo.file_id)).data.url
        : photo.url || photoUrlCache.current[cacheKey]
          || (photo.photo_id ? (await activitiesApi.getPhotoUrl(photo.photo_id)).data.url : '');

      if (cacheKey && url) {
        photoUrlCache.current[cacheKey] = url;
      }
      
      setPhotoPreview({
        open: true,
        url: url,
        title: photo.file_name,
        allPhotos,
        currentIndex: currentIndex >= 0 ? currentIndex : 0
      });
    } catch (error) {
      console.error('Error loading photo preview:', error);
    }
  };

  const handlePhotoNavigate = async (direction) => {
    if (!photoPreview.allPhotos || photoPreview.allPhotos.length === 0) return;
    
    let newIndex;
    if (direction === 'next') {
      newIndex = (photoPreview.currentIndex + 1) % photoPreview.allPhotos.length;
    } else {
      newIndex = (photoPreview.currentIndex - 1 + photoPreview.allPhotos.length) % photoPreview.allPhotos.length;
    }
    
    const newPhoto = photoPreview.allPhotos[newIndex];

    try {
      const cacheKey = newPhoto.file_id ? `file-${newPhoto.file_id}` : newPhoto.photo_id;
      const url = newPhoto.file_id && !readOnly
        ? (await filesApi.getUrl(newPhoto.file_id)).data.url
        : newPhoto.url || photoUrlCache.current[cacheKey]
          || (newPhoto.photo_id ? (await activitiesApi.getPhotoUrl(newPhoto.photo_id)).data.url : '');

      if (cacheKey && url) {
        photoUrlCache.current[cacheKey] = url;
      }

      setPhotoPreview(prev => ({
        ...prev,
        url,
        title: newPhoto.file_name,
        currentIndex: newIndex
      }));
    } catch (error) {
      console.error('Error navigating activity photo preview:', error);
    }
  };

  const handleDocumentOpen = async (document) => {
    if (readOnly) return;
    try {
      const response = await filesApi.getUrl(document.file_id);
      const url = response.data.url;
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    } catch (error) {
      console.error('Error opening activity document:', error);
    }
  };

  const handleOpenForm = async (form) => {
    if (!form) {
      return;
    }

    if (typeof form === 'object') {
      setSelectedForm(form);
      return;
    }

    const projectId = activity?.project_id || initialActivityData?.project_id;
    if (!projectId) {
      console.error('Cannot open form details without a project ID');
      return;
    }

    try {
      setLoadingForm(true);
      const response = await projectFormsApi.getById(projectId, form);
      setSelectedForm(response.data);
    } catch (error) {
      console.error('Error loading form details:', error);
    } finally {
      setLoadingForm(false);
    }
  };

  const handleCloseForm = () => {
    setSelectedForm(null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!activity) {
    return (
      <Box sx={{ p: 2, color: 'text.secondary' }}>
        Activity details unavailable.
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', overflow: 'auto', p: 2 }}>
      <ActivityCard
        activity={activity}
        expandedActivity={activity.activity_id} // Always expanded
        activityNotes={{ [activity.activity_id]: notes }}
        activityPhotos={{ [activity.activity_id]: photos }}
        newNotes={{ [activity.activity_id]: newNote }}
        selectedPhotos={{ [activity.activity_id]: selectedPhotos }}
        showMentions={{ [activity.activity_id]: showMentions }}
        mentionSuggestions={{ [activity.activity_id]: mentionSuggestions }}
        workers={workers}
        uploading={{ [activity.activity_id]: uploading }}
        loadingNotes={{ [activity.activity_id]: loadingNotes }}
        loadingPhotos={{ [activity.activity_id]: loadingPhotos }}
        completingActivity={completingActivity}
        onToggleComplete={readOnly ? null : handleToggleComplete}
        onEdit={readOnly ? null : ((activity) => {
          if (onEdit) {
            onEdit(activity);
          }
        })}
        onDelete={readOnly ? null : onDelete}
        onActivityExpand={() => {}} // No-op since always expanded
        onPhotoPreview={handlePhotoPreview}
        onDocumentOpen={readOnly ? null : handleDocumentOpen}
        onViewPlan={onOpenPlan || (readOnly ? null : (_activity, plan) => handleDocumentOpen(plan))}
        onNoteChange={readOnly ? null : handleNoteChange}
        onMentionInput={readOnly ? null : handleMentionInput}
        onPhotoSelect={readOnly ? null : handlePhotoSelect}
        onNoteSubmit={readOnly ? null : handleNoteSubmit}
        onRemovePhoto={readOnly ? null : handleRemovePhoto}
        onInsertMention={readOnly ? null : handleInsertMention}
        onOpenForm={handleOpenForm}
        alwaysExpanded={true}
        hideViewPlanButton={true}
        readOnly={readOnly}
        resolvePublicPhotoUrl={resolvePublicPhotoUrl}
      />

      <Dialog
        open={Boolean(selectedForm) || loadingForm}
        onClose={handleCloseForm}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ pr: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
            <Box>
              <Typography variant="h6" component="div">
                {selectedForm?.title || selectedForm?.template_name || 'Form'}
              </Typography>
              {selectedForm?.worker_name && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Completed by {selectedForm.worker_name}
                </Typography>
              )}
            </Box>
            <IconButton
              aria-label="close form viewer"
              onClick={handleCloseForm}
              edge="end"
              size="small"
              sx={{ mt: 0.25 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingForm ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : selectedForm && getFormFields(selectedForm).length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {getFormFields(selectedForm).map((field, index) => {
                const value = getFormValue(selectedForm.form_data, field);
                return (
                  <React.Fragment key={field.id || field.name || `${field.label}-${index}`}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {field.label || field.name || 'Field'}
                      </Typography>
                      {renderReadOnlyFormField(field, value)}
                    </Paper>
                    {index < getFormFields(selectedForm).length - 1 && <Divider sx={{ display: 'none' }} />}
                  </React.Fragment>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Form details are unavailable for this item.
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* Photo Preview */}
      <ImageViewer
        open={photoPreview.open}
        onClose={() => setPhotoPreview({ open: false, url: '', title: '', allPhotos: [], currentIndex: 0 })}
        imageUrl={photoPreview.url}
        fileName={photoPreview.title}
        allImages={photoPreview.allPhotos || []}
        currentIndex={photoPreview.currentIndex || 0}
        onNavigate={handlePhotoNavigate}
      />
    </Box>
  );
};

export default ActivityDetails;
